import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import { addOrUpdateSalaryConfig, SALARY_TYPES } from "../services/salaryService";
import { getEmployees } from "../services/userService";
import { toast } from "react-toastify";

const SalaryForm = ({ show, onHide, onSuccess, editData = null }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    salaryType: SALARY_TYPES.MONTHLY,
    baseSalary: "",
    allowances: "",
    deductions: "",
    overtimeRate: "",
    effectiveDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (editData) {
      setFormData({
        userId: editData.userId || "",
        userName: editData.userName || "",
        salaryType: editData.salaryType || SALARY_TYPES.MONTHLY,
        baseSalary: editData.baseSalary?.toString() || "",
        allowances: editData.allowances?.toString() || "",
        deductions: editData.deductions?.toString() || "",
        overtimeRate: editData.overtimeRate?.toString() || "",
        effectiveDate: editData.effectiveDate || new Date().toISOString().split("T")[0],
      });
    } else {
      resetForm();
    }
  }, [editData, show]);

  const fetchEmployees = async () => {
    try {
      const allUsers = await getEmployees();
      const activeUsers = allUsers.filter((user) => user.isActive);
      setEmployees(activeUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    }
  };

  const resetForm = () => {
    setFormData({
      userId: "",
      userName: "",
      salaryType: SALARY_TYPES.MONTHLY,
      baseSalary: "",
      allowances: "",
      deductions: "",
      overtimeRate: "",
      effectiveDate: new Date().toISOString().split("T")[0],
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Auto-fill userName when userId is selected
    if (name === "userId") {
      const selectedEmployee = employees.find((emp) => emp.id === value);
      if (selectedEmployee) {
        setFormData((prev) => ({
          ...prev,
          userName: selectedEmployee.name,
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.userId || !formData.baseSalary) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const salaryConfig = {
        userId: formData.userId,
        userName: formData.userName,
        salaryType: formData.salaryType,
        baseSalary: parseFloat(formData.baseSalary),
        allowances: parseFloat(formData.allowances) || 0,
        deductions: parseFloat(formData.deductions) || 0,
        overtimeRate: parseFloat(formData.overtimeRate) || 0,
        effectiveDate: formData.effectiveDate,
      };

      await addOrUpdateSalaryConfig(salaryConfig);
      onSuccess();
      onHide();
      resetForm();
    } catch (error) {
      console.error("Error saving salary configuration:", error);
      toast.error("Failed to save salary configuration");
    }
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editData ? "Edit Salary Configuration" : "Add Salary Configuration"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>User *</Form.Label>
                <Form.Select
                  name="userId"
                  value={formData.userId}
                  onChange={handleInputChange}
                  required
                  disabled={editData} // Disable if editing
                >
                  <option value="">Select User</option>
                  {employees.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email}) - {user.role?.toUpperCase()}
                      {user.studentLevel && ` - ${user.studentLevel.replace('_', ' ').toUpperCase()}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Salary Type *</Form.Label>
                <Form.Select
                  name="salaryType"
                  value={formData.salaryType}
                  onChange={handleInputChange}
                  required
                >
                  {Object.values(SALARY_TYPES).map((type) => (
                    <option key={type} value={type}>
                      {type.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Base Salary * 
                  {formData.salaryType === SALARY_TYPES.HOURLY && " (per hour)"}
                  {formData.salaryType === SALARY_TYPES.MONTHLY && " (per month)"}
                </Form.Label>
                <Form.Control
                  type="number"
                  name="baseSalary"
                  value={formData.baseSalary}
                  onChange={handleInputChange}
                  placeholder="Enter base salary"
                  min="0"
                  step="0.01"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Allowances</Form.Label>
                <Form.Control
                  type="number"
                  name="allowances"
                  value={formData.allowances}
                  onChange={handleInputChange}
                  placeholder="Enter allowances"
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Deductions</Form.Label>
                <Form.Control
                  type="number"
                  name="deductions"
                  value={formData.deductions}
                  onChange={handleInputChange}
                  placeholder="Enter deductions"
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Overtime Rate (per hour)</Form.Label>
                <Form.Control
                  type="number"
                  name="overtimeRate"
                  value={formData.overtimeRate}
                  onChange={handleInputChange}
                  placeholder="Enter overtime rate"
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  name="effectiveDate"
                  value={formData.effectiveDate}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="bg-light p-3 rounded">
            <h6>Salary Preview</h6>
            <div className="row">
              <div className="col-md-6">
                <small><strong>Base Salary:</strong> ₹{formData.baseSalary || 0}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Allowances:</strong> ₹{formData.allowances || 0}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Deductions:</strong> ₹{formData.deductions || 0}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Overtime Rate:</strong> ₹{formData.overtimeRate || 0}/hr</small>
              </div>
            </div>
            <hr />
            <div className="text-center">
              <strong>
                Estimated Net Salary: ₹
                {(parseFloat(formData.baseSalary) || 0) + 
                 (parseFloat(formData.allowances) || 0) - 
                 (parseFloat(formData.deductions) || 0)}
              </strong>
              <br />
              <small className="text-muted">
                (Excluding overtime and taxes)
              </small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? "Saving..." : editData ? "Update Configuration" : "Add Configuration"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SalaryForm;